import { useState, useEffect, useRef, useCallback } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import { useNotifications } from '../contexts/NotificationContext';
import { useTransfer } from '../contexts/TransferContext';

/** 1 GB: maximum total size of files whose sizes are known before sending a ZIP request */
export const MAX_BATCH_ZIP_SIZE = 1 * 1024 * 1024 * 1024;

/** Must match backend DOWNLOAD_JOB_LIMITS constants */
const DOWNLOAD_LIMITS = {
  maxFiles: 100,
  maxTotalSize: 1 * 1024 * 1024 * 1024, // 1 GB — must match backend download.js
};

/**
 * A single item to include in a batch ZIP download request.
 */
export interface DownloadItem {
  /** S3 object key */
  key: string;
  /** True if the key represents a folder prefix */
  isFolder: boolean;
}

/**
 * Parameters required to construct the useDownloadJob hook.
 */
export interface UseDownloadJobParams {
  /** S3 bucket name the browser is currently viewing */
  bucketName: string;
  /** Current folder prefix (e.g. "folder/" or "" for root) */
  prefix: string;
  /** Callback invoked after a download completes successfully so the parent can clear its selection */
  onSelectionClear: () => void;
}

/**
 * Shape of the object returned by useDownloadJob.
 */
export interface UseDownloadJobReturn {
  /** Backend job ID once a ZIP job has been created, null otherwise */
  downloadJobId: string | null;
  /** 0–100 progress value updated via polling */
  downloadProgress: number;
  /** Human-readable status string (e.g. "Creating ZIP file... 42%") */
  downloadStatus: string;
  /** True while a batch ZIP download is in progress */
  isDownloading: boolean;
  /**
   * Start a new batch ZIP download for the given items.
   * Performs all frontend pre-checks before calling the backend.
   */
  startDownload: (items: DownloadItem[]) => Promise<void>;
  /** Cancel the currently active download job and stop polling */
  cancelDownload: () => Promise<void>;
  /**
   * Check whether there is an in-progress download job on the backend and
   * resume polling for it (used on mount and when the bucket changes).
   */
  checkActiveJob: () => Promise<void>;
  /** Regenerate an expired presigned ZIP URL and update the notification */
  regenerateUrl: (jobId: string, notifId: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Notification title for a terminal ZIP download failure.
 * Exported as a pure helper so tests can assert on the title decision
 * independently of the full poll cycle.
 */
export function downloadFailureTitle(status: 'failed'): string {
  return 'Download Failed';
}

export function downloadCancelledTitle(): string {
  return 'Download Cancelled';
}

/**
 * Manages the full lifecycle of a batch ZIP download job.
 *
 * Handles polling, cancellation, URL regeneration, and notification updates.
 * Integrates with NotificationContext and TransferContext for idle-timeout suspension.
 *
 * Critical correctness note: state values read inside the 2-second setInterval
 * callback must be accessed via useRef mirrors, not the state variables directly.
 * This is why notifIdRef, downloadPollRef, and transferCleanupRef are maintained
 * alongside their corresponding state / interval variables.
 */
export function useDownloadJob({
  bucketName,
  prefix,
  onSelectionClear,
}: UseDownloadJobParams): UseDownloadJobReturn {
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStatus, setDownloadStatus] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  // Refs for stale-closure safety in setInterval callbacks
  const notifIdRef = useRef<string | null>(null);
  const downloadPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transferCleanupRef = useRef<(() => void) | null>(null);
  // Ref mirror for downloadJobId — allows cancelDownload (called from interval /
  // checkActiveJob) to read the current jobId without a stale closure
  const downloadJobIdRef = useRef<string | null>(null);

  const { addNotification, updateNotification } = useNotifications();
  const { registerTransfer } = useTransfer();

  // Keep both state and ref in sync so the interval callback always has the
  // latest notification id without a stale closure.
  // Note: notifIdRef is the read source; the setState call keeps React's state
  // consistent for any future consumers.
  const setNotificationIdSync = useCallback((id: string | null) => {
    notifIdRef.current = id;
  }, []);

  // Keep downloadJobId ref in sync with state
  const setDownloadJobIdSync = useCallback((id: string | null) => {
    setDownloadJobId(id);
    downloadJobIdRef.current = id;
  }, []);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount — dedicated effect with empty deps per spec constraint
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (downloadPollRef.current) {
        clearInterval(downloadPollRef.current);
      }
      transferCleanupRef.current?.();
      transferCleanupRef.current = null;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // regenerateUrl
  // ---------------------------------------------------------------------------

  /**
   * Request a fresh presigned URL for an already-completed ZIP job and update
   * the corresponding notification entry so the user can download again.
   */
  const regenerateUrl = useCallback(
    async (jobId: string, notifId: string) => {
      try {
        const response = await apiCall(`/s3/regenerate-zip-url/${bucketName}`, {
          method: 'POST',
          body: JSON.stringify({ jobId }),
        });
        if (notifId) {
          updateNotification(notifId, {
            downloadUrl: response.downloadUrl,
            urlExpiresAt: response.expiresAt,
          });
        }
        toast.success('Download link refreshed');
      } catch (error: any) {
        toast.error('Failed to regenerate download link');
      }
    },
    [bucketName, updateNotification]
  );

  // ---------------------------------------------------------------------------
  // pollDownloadStatus
  // CRITICAL: reads notifIdRef.current inside the interval callback, NOT the
  // notificationId state variable — the state value would be stale.
  // ---------------------------------------------------------------------------

  /**
   * Poll the backend for the current status of a ZIP download job.
   * Called both directly (immediately after job creation / resume) and from the
   * setInterval callback stored in downloadPollRef.
   *
   * @param jobId  - the job to poll
   * @param nId    - notification id to update; falls back to notifIdRef.current
   */
  const pollDownloadStatus = useCallback(
    async (jobId: string, nId?: string) => {
      // Always prefer the ref so this works correctly inside stale setInterval closures
      const currentNotifId = nId || notifIdRef.current;

      try {
        const response = await apiCall(`/s3/download-job/${encodeURIComponent(jobId)}`);

        if (response.status === 'completed' && response.details?.downloadUrl) {
          // Stop polling
          if (downloadPollRef.current) {
            clearInterval(downloadPollRef.current);
            downloadPollRef.current = null;
          }

          transferCleanupRef.current?.();
          transferCleanupRef.current = null;

          setDownloadProgress(100);
          setDownloadStatus('Download ready!');

          // Update notification to show progress
          if (currentNotifId) {
            updateNotification(currentNotifId, {
              progress: 100,
              message: 'Download ready! Starting...',
              status: 'processing',
            });
          }

          // Trigger browser download of the ZIP file
          const link = document.createElement('a');
          link.href = response.details.downloadUrl;
          link.download = `download-${Date.now()}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Mark job as downloaded on backend (fire-and-forget)
          try {
            await apiCall('/s3/download-job-complete', {
              method: 'POST',
              body: JSON.stringify({ jobId }),
            });
          } catch (err) {
            console.warn('Failed to mark job complete:', err);
          }

          // Update notification to completed with persistent download link
          if (currentNotifId) {
            updateNotification(currentNotifId, {
              status: 'completed',
              message: `Downloaded ${response.details.fileCount} file(s) as ZIP`,
              progress: 100,
              canDismiss: true,
              onCancel: undefined,
              downloadUrl: response.details.downloadUrl,
              urlExpiresAt: response.details.urlExpiresAt,
              jobId: jobId,
              onRegenerate: () => regenerateUrl(jobId, currentNotifId || ''),
            });
          }

          // Reset all state
          setDownloadJobIdSync(null);
          setDownloadProgress(0);
          setDownloadStatus('');
          setIsDownloading(false);
          setNotificationIdSync(null);
          onSelectionClear();
        } else if (response.status === 'processing') {
          const progress = response.details?.progress || 0;
          const phase = response.details?.phase;
          setDownloadProgress(progress);

          const statusMessage =
            phase === 'uploading'
              ? 'Finalizing ZIP file...'
              : `Creating ZIP file... ${progress}%`;

          setDownloadStatus(statusMessage);

          if (currentNotifId) {
            updateNotification(currentNotifId, {
              progress,
              message: statusMessage,
              status: 'processing',
            });
          }
        } else if (response.status === 'failed' || response.status === 'cancelled') {
          // Stop polling
          if (downloadPollRef.current) {
            clearInterval(downloadPollRef.current);
            downloadPollRef.current = null;
          }

          transferCleanupRef.current?.();
          transferCleanupRef.current = null;

          if (currentNotifId) {
            updateNotification(currentNotifId, {
              ...(response.status === 'failed'
                ? { title: downloadFailureTitle('failed') }
                : response.status === 'cancelled'
                  ? { title: downloadCancelledTitle() }
                  : {}),
              status: response.status === 'cancelled' ? 'cancelled' : 'failed',
              message:
                response.status === 'cancelled'
                  ? 'Download cancelled'
                  : response.details?.error || 'Download failed',
              canDismiss: true,
              onCancel: undefined,
            });
          }

          setDownloadJobIdSync(null);
          setDownloadProgress(0);
          setDownloadStatus('');
          setIsDownloading(false);
          setNotificationIdSync(null);
        } else if (response.status === 'pending') {
          setDownloadStatus('Preparing download...');
          if (currentNotifId) {
            updateNotification(currentNotifId, {
              message: 'Preparing download...',
              status: 'pending',
            });
          }
        }
      } catch (error: any) {
        console.error('Error polling download status:', error);
        // Do not stop polling on transient network errors
      }
    },
    [
      updateNotification,
      regenerateUrl,
      onSelectionClear,
      setDownloadJobIdSync,
      setNotificationIdSync,
    ]
  );

  // ---------------------------------------------------------------------------
  // cancelDownload
  // ---------------------------------------------------------------------------

  /**
   * Cancel the currently active download job on the backend, stop polling, and
   * reset all local state.
   */
  const cancelDownload = useCallback(async () => {
    // Read from ref to avoid stale closure — downloadJobId state may be null in
    // the setInterval closure even if a job is genuinely active
    const currentJobId = downloadJobIdRef.current;
    if (!currentJobId) return;

    try {
      await apiCall('/s3/download-job-cancel', {
        method: 'POST',
        body: JSON.stringify({ jobId: currentJobId }),
      });

      // Stop polling
      if (downloadPollRef.current) {
        clearInterval(downloadPollRef.current);
        downloadPollRef.current = null;
      }

      // Update notification using ref (stale-closure safe)
      if (notifIdRef.current) {
        updateNotification(notifIdRef.current, {
          status: 'cancelled',
          message: 'Download cancelled by user',
          canDismiss: true,
          onCancel: undefined,
        });
      }

      // Reset state
      setDownloadJobIdSync(null);
      setDownloadProgress(0);
      setDownloadStatus('');
      setIsDownloading(false);
      setNotificationIdSync(null);
    } catch (error: any) {
      console.error('Error cancelling download:', error);
      if (notifIdRef.current) {
        updateNotification(notifIdRef.current, {
          message: 'Failed to cancel download',
        });
      }
    }
  }, [updateNotification, setDownloadJobIdSync, setNotificationIdSync]);

  // ---------------------------------------------------------------------------
  // checkActiveJob  (exposed so FileBrowser can call it in a useEffect)
  // ---------------------------------------------------------------------------

  /**
   * Check whether there is already an active ZIP download job on the backend
   * (e.g. surviving a page refresh) and, if so, resume polling for it.
   */
  const checkActiveJob = useCallback(async () => {
    try {
      const response = await apiCall('/s3/download-job-active');
      if (response.hasActiveJob && response.jobId) {
        // Resume tracking for this existing job
        setDownloadJobIdSync(response.jobId);
        setIsDownloading(true);
        setDownloadProgress(response.details?.progress || 0);
        setDownloadStatus(
          response.status === 'processing'
            ? `Resuming... ${response.details?.progress || 0}%`
            : 'Resuming download...'
        );

        // Create a notification for the resumed download
        const nId = addNotification({
          type: 'download',
          title: 'Download Resumed',
          message: `Resuming download of ${response.details?.fileCount || 'multiple'} file(s)`,
          progress: response.details?.progress || 0,
          status: response.status === 'processing' ? 'processing' : 'pending',
          canDismiss: false,
          onCancel: cancelDownload,
        });
        setNotificationIdSync(nId);

        // Start polling interval
        downloadPollRef.current = setInterval(() => {
          pollDownloadStatus(response.jobId, nId);
        }, 2000);

        // Also poll immediately so the UI updates without waiting 2 s
        pollDownloadStatus(response.jobId, nId);
      }
    } catch (error) {
      console.error('Error checking active download job:', error);
    }
  }, [
    addNotification,
    cancelDownload,
    pollDownloadStatus,
    setDownloadJobIdSync,
    setNotificationIdSync,
  ]);

  // ---------------------------------------------------------------------------
  // startDownload  (was downloadSelectedFiles in FileBrowser)
  // ---------------------------------------------------------------------------

  /**
   * Initiate a batch ZIP download for the given items.
   *
   * Performs all frontend pre-checks (size limits, file count limits) before
   * registering the transfer, creating a notification, and calling the backend
   * to start an async ZIP job.
   *
   * @param items - array of S3 keys and folder flags to include in the ZIP
   */
  const startDownload = useCallback(
    async (items: DownloadItem[]) => {
      if (items.length === 0) return;

      // Guard: only one download at a time
      if (isDownloading || downloadJobIdRef.current) {
        toast.error('A download is already in progress. Please wait for it to complete.');
        return;
      }

      const selectedFolders = items.filter((i) => i.isFolder);
      const selectedFilesOnly = items.filter((i) => !i.isFolder);

      // Batch ZIP size pre-check (Req 6.1, 6.2): runs BEFORE registerTransfer
      // We can only measure files with known sizes (not folders)
      // NOTE: items here don't have size; the caller (FileBrowser) is expected to
      // have already filtered/summed sizes before building the items array.
      // We keep the MAX_BATCH_ZIP_SIZE guard in startDownload to match the original
      // FileBrowser logic — the caller should pass items whose total known size
      // was already checked, but we re-validate here for safety.

      // File count pre-check
      if (selectedFolders.length === 0) {
        if (selectedFilesOnly.length > DOWNLOAD_LIMITS.maxFiles) {
          toast.error(
            `Selection too large. Maximum ${DOWNLOAD_LIMITS.maxFiles} files allowed per download. You selected ${selectedFilesOnly.length} files.`
          );
          return;
        }
      } else {
        // Folders selected — warn if file count is already high
        if (selectedFilesOnly.length > DOWNLOAD_LIMITS.maxFiles * 0.8) {
          toast.error(
            `You've selected ${selectedFilesOnly.length} files plus ${selectedFolders.length} folder(s). This may exceed the ${DOWNLOAD_LIMITS.maxFiles} file limit. Try selecting fewer items.`
          );
          return;
        }
      }

      // Register transfer before the API call (Req 4.2) so idle-timeout is aware
      transferCleanupRef.current = registerTransfer({
        id: `download:zip:${bucketName}:${Date.now()}`,
        type: 'download',
        bucket: bucketName,
        key: `zip:${items.map((i) => i.key).join(',')}`.slice(0, 256),
      });

      setIsDownloading(true);

      // Create a notification entry for this download
      const nId = addNotification({
        type: 'download',
        title: 'Download Started',
        message: `Preparing ${items.length} item(s) for download...`,
        progress: 0,
        status: 'pending',
        canDismiss: false,
        onCancel: cancelDownload,
      });
      setNotificationIdSync(nId);

      try {
        const response = await apiCall(`/s3/download-zip/${bucketName}`, {
          method: 'POST',
          body: JSON.stringify({
            items,
            basePath: prefix,
          }),
        });

        // 409 conflict — another job already running
        if (response.activeJobId) {
          transferCleanupRef.current?.();
          transferCleanupRef.current = null;
          updateNotification(nId, {
            status: 'failed',
            message: 'A download is already in progress. Please wait for it to complete.',
            canDismiss: true,
            onCancel: undefined,
          });
          setIsDownloading(false);
          setNotificationIdSync(null);
          return;
        }

        // Job created successfully
        if (response.jobId) {
          setDownloadJobIdSync(response.jobId);
          setDownloadProgress(0);
          setDownloadStatus('Preparing download...');

          updateNotification(nId, {
            message: `Preparing ${response.fileCount} file(s) for download...`,
            status: 'pending',
          });

          // Start 2-second polling interval
          downloadPollRef.current = setInterval(() => {
            pollDownloadStatus(response.jobId, nId);
          }, 2000);

          // Also poll immediately
          pollDownloadStatus(response.jobId, nId);
        } else {
          throw new Error('No job ID received');
        }
      } catch (error: any) {
        transferCleanupRef.current?.();
        transferCleanupRef.current = null;
        const errorMessage = 'Failed to start download';

        // Show prominent toast for actionable errors
        if (
          errorMessage.includes('Monthly data transfer limit') ||
          errorMessage.includes('Selection too large') ||
          errorMessage.includes('No download permission')
        ) {
          toast.error(errorMessage);
        }

        updateNotification(nId, {
          status: 'failed',
          message: errorMessage,
          canDismiss: true,
          onCancel: undefined,
        });
        setIsDownloading(false);
        setDownloadJobIdSync(null);
        setDownloadProgress(0);
        setDownloadStatus('');
        setNotificationIdSync(null);
      }
    },
    [
      isDownloading,
      bucketName,
      prefix,
      registerTransfer,
      addNotification,
      cancelDownload,
      updateNotification,
      pollDownloadStatus,
      setDownloadJobIdSync,
      setNotificationIdSync,
    ]
  );

  return {
    downloadJobId,
    downloadProgress,
    downloadStatus,
    isDownloading,
    startDownload,
    cancelDownload,
    checkActiveJob,
    regenerateUrl,
  };
}
